import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import Header from '../Header';
import LeadsPage from '../LeadsPage';
import ProcessSystemsView from '../ProcessSystemsView';
import BackofficeExamsCalendar from '../BackofficeExamsCalendar';
import ErfolgeTab from '../ErfolgeTab';
import UserCreationModal from './UserCreationModal';
import AnalyticsTab from './AnalyticsTab';
import AdminSchulungenView from './AdminSchulungenView';
import OverviewTab from './tabs/OverviewTab';
import CategoriesTab from './tabs/CategoriesTab';
import ModulesTab from './tabs/ModulesTab';
import UsersTab from './tabs/UsersTab';
import UserModals from './modals/UserModals';
import CategoryModals from './modals/CategoryModals';
import ModuleModals from './modals/ModuleModals';
import WikiModals from './modals/WikiModals';

const AdminPanel = () => {
  const { user } = useAuth();
  const [categories, setCategories] = useState([]);
  const [modules, setModules] = useState([]);
  const [users, setUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddModule, setShowAddModule] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showDeleteUser, setShowDeleteUser] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingModule, setEditingModule] = useState(null);
  const [deletingModule, setDeletingModule] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserData, setEditUserData] = useState({ name: '', email: '', role: '', managed_by: '' });
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [showEmptyCategories, setShowEmptyCategories] = useState(true);
  const [showDeleteCategory, setShowDeleteCategory] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [deleteCategoryOption, setDeleteCategoryOption] = useState('move'); // 'move', 'delete', 'cancel'
  const [targetCategoryId, setTargetCategoryId] = useState('');
  const [newCategory, setNewCategory] = useState({
    title: '',
    description: '',
    color: '#3B82F6',
    icon: '',
    target_roles: ['vertriebler'],
    visibility_mode: 'cascade'
  });
  const [newModule, setNewModule] = useState({
    title: '',
    description: '',
    category_id: '',
    content_type: 'text',
    content: '',
    duration_minutes: null,
    order: 0
  });

  // Drag & Drop state
  const [draggedCategoryIndex, setDraggedCategoryIndex] = useState(null);
  const [draggedModuleIndex, setDraggedModuleIndex] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('overview');
  
  // Leads-Navigation aus Benachrichtigungs-Glocke
  React.useEffect(() => {
    const handler = () => setActiveTab('leads');
    window.addEventListener('navigateToLeads', handler);
    return () => window.removeEventListener('navigateToLeads', handler);
  }, []);
  
  // Loading state
  const [loading, setLoading] = useState(false);

  // Termine (Abschlussgespräche) state for Admin
  const [adminAppointments, setAdminAppointments] = useState([]);
  const [resettingReschedule, setResettingReschedule] = useState(null);

  const handleResetReschedule = async (userId, userName) => {
    setResettingReschedule(userId);
    try {
      await axios.post(`${API}/appointments/users/${userId}/reset-reschedule`);
      toast.success(`Umbuchungs-Option für ${userName} zurückgesetzt`);
      const r = await axios.get(`${API}/backoffice/dashboard`);
      setAdminAppointments(r.data?.appointments ?? []);
    } catch(e) { toast.error('Fehler beim Zurücksetzen'); }
    setResettingReschedule(null);
  };
  
  // Category PDF Management state
  const [categoryPdfs, setCategoryPdfs] = useState({});
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedCategoryForPdf, setSelectedCategoryForPdf] = useState(null);
  const [pdfUploadFile, setPdfUploadFile] = useState(null);
  const [pdfDescription, setPdfDescription] = useState('');
  const [pdfUploading, setPdfUploading] = useState(false);
  
  // Process Systems state for Admin
  const [selectedProcessSystem, setSelectedProcessSystem] = useState(null);
  
  // Modal states for Process Systems
  const [showSystemModal, setShowSystemModal] = useState(false);
  const [showGuideModal, setShowGuideModal] = useState(false);
  const [showFaqModal, setShowFaqModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  
  // Form states for Process Systems
  const [systemForm, setSystemForm] = useState({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
  const [guideForm, setGuideForm] = useState({ title: '', description: '', steps: [], order: 0 });
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '', order: 0 });
  
  // Editing states for Process Systems
  const [editingSystem, setEditingSystem] = useState(null);
  const [editingGuide, setEditingGuide] = useState(null);
  const [editingFaq, setEditingFaq] = useState(null);
  
  // Upload states for Process Systems
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // Module search and filter states
  const [moduleSearchTerm, setModuleSearchTerm] = useState('');
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('');
  const [selectedTypeFilter, setSelectedTypeFilter] = useState('');
  const [sortField, setSortField] = useState('order');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Category accordion state - expanded by default
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  
  // Phase 2: Bulk operations state
  const [selectedModules, setSelectedModules] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionType, setBulkActionType] = useState('');
  const [bulkTargetCategory, setBulkTargetCategory] = useState('');
  
  // Phase 1: Category management enhancements
  const [categorySearchTerm, setCategorySearchTerm] = useState('');
  const [selectedStatusFilter, setSelectedStatusFilter] = useState('');
  const [selectedModuleCountFilter, setSelectedModuleCountFilter] = useState('');
  const [categorySortField, setCategorySortField] = useState('order');
  const [categorySortDirection, setCategorySortDirection] = useState('asc');
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [showCategoryBulkActions, setShowCategoryBulkActions] = useState(false);
  const [bulkCategoryActionType, setBulkCategoryActionType] = useState('');
  const [bulkStartPosition, setBulkStartPosition] = useState(1);
  const [bulkCategoryColor, setBulkCategoryColor] = useState('#3B82F6');
  
  // Phase 3: Category analytics state
  const [showCategoryAnalytics, setShowCategoryAnalytics] = useState(false);
  const [selectedAnalyticsCategory, setSelectedAnalyticsCategory] = useState(null);
  const [categoryAnalyticsData, setCategoryAnalyticsData] = useState({});
  
  // PDF Management state - using existing variables from lines 7785-7790
  const [showPdfListModal, setShowPdfListModal] = useState(false);
  
  // PDF List Modal Handler
  const openPdfListModal = (category) => {
    setSelectedCategoryForPdf(category);
    setShowPdfListModal(true);
  };
  
  // Notification state
  const [notification, setNotification] = useState(null);
  
  // Phase 2: Bulk operations functions
  const toggleModuleSelection = (moduleId) => {
    const newSelection = new Set(selectedModules);
    if (newSelection.has(moduleId)) {
      newSelection.delete(moduleId);
    } else {
      newSelection.add(moduleId);
    }
    setSelectedModules(newSelection);
  };

  const selectAllModules = () => {
    const allModuleIds = new Set(getFilteredModules().map(m => m.id));
    setSelectedModules(allModuleIds);
  };

  const deselectAllModules = () => {
    setSelectedModules(new Set());
  };

  const handleBulkAction = async () => {
    if (selectedModules.size === 0) return;

    try {
      const moduleIds = Array.from(selectedModules);
      
      switch (bulkActionType) {
        case 'delete':
          for (const moduleId of moduleIds) {
            await axios.delete(`${API}/modules/${moduleId}`);
          }
          showSuccessNotification(`${moduleIds.length} Module erfolgreich gelöscht`);
          break;
          
        case 'changeCategory':
          if (!bulkTargetCategory) return;
          for (const moduleId of moduleIds) {
            await axios.put(`${API}/modules/${moduleId}`, {
              category_id: bulkTargetCategory
            });
          }
          showSuccessNotification(`${moduleIds.length} Module in neue Kategorie verschoben`);
          break;
          
        case 'activate':
          for (const moduleId of moduleIds) {
            await axios.put(`${API}/modules/${moduleId}`, {
              is_active: true
            });
          }
          showSuccessNotification(`${moduleIds.length} Module aktiviert`);
          break;
          
        case 'deactivate':
          for (const moduleId of moduleIds) {
            await axios.put(`${API}/modules/${moduleId}`, {
              is_active: false
            });
          }
          showSuccessNotification(`${moduleIds.length} Module deaktiviert`);
          break;
      }
      
      // Refresh data
      const modulesRes = await axios.get(`${API}/modules`);
      setModules(modulesRes.data);
      
      // Clear selection
      setSelectedModules(new Set());
      setShowBulkActions(false);
      setBulkActionType('');
      setBulkTargetCategory('');
      
    } catch (error) {
      console.error('Bulk action error:', error);
      showErrorNotification('Fehler bei der Massenoperation');
    }
  };

  // Phase 2: Additional module actions
  const duplicateModule = async (module) => {
    try {
      const duplicatedModule = {
        ...module,
        title: `${module.title} (Kopie)`,
        order: module.order + 1
      };
      delete duplicatedModule.id;
      delete duplicatedModule.created_at;
      
      await axios.post(`${API}/modules`, duplicatedModule);
      showSuccessNotification('Modul erfolgreich dupliziert');
      
      // Refresh modules
      const modulesRes = await axios.get(`${API}/modules`);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Module duplication error:', error);
      showErrorNotification('Fehler beim Duplizieren des Moduls');
    }
  };


  const toggleModuleStatus = async (module) => {
    try {
      await axios.put(`${API}/modules/${module.id}`, {
        is_active: !module.is_active
      });
      
      showSuccessNotification(`Modul ${module.is_active ? 'deaktiviert' : 'aktiviert'}`);
      
      // Refresh modules
      const modulesRes = await axios.get(`${API}/modules`);
      setModules(modulesRes.data);
    } catch (error) {
      console.error('Module status toggle error:', error);
      showErrorNotification('Fehler beim Ändern des Modul-Status');
    }
  };

  // Phase 3: Analytics and advanced functions
  const [moduleAnalytics, setModuleAnalytics] = useState({});
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [selectedAnalyticsModule, setSelectedAnalyticsModule] = useState(null);
  const [moduleAnalyticsData, setModuleAnalyticsData] = useState(null);
  const [loadingModuleAnalytics, setLoadingModuleAnalytics] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewModule, setPreviewModule] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);

  const [moduleCompletionRates, setModuleCompletionRates] = useState({});
  const getModuleCompletionRate = (moduleId) => moduleCompletionRates[moduleId]?.rate ?? 0;

  const showModuleAnalytics = async (module) => {
    setSelectedAnalyticsModule(module);
    setShowAnalyticsModal(true);
    setModuleAnalyticsData(null);
    setLoadingModuleAnalytics(true);
    try {
      const res = await axios.get(`${API}/modules/${module.id}/analytics`);
      setModuleAnalyticsData(res.data);
    } catch (e) {
      console.error('Error fetching module analytics:', e);
    } finally {
      setLoadingModuleAnalytics(false);
    }
  };

  const openPreviewModal = async (module) => {
    setPreviewModule(module);
    setShowPreviewModal(true);
    setPreviewLoading(true);
    
    try {
      // Load additional content based on module type
      if (module.content_type === 'pdf') {
        try {
          // Try to get PDF slides if they exist
          const slidesResponse = await axios.get(`${API}/modules/${module.id}/pdf-slides`);
          if (slidesResponse.status === 200) {
            setPreviewModule(prev => ({
              ...prev,
              pdfSlides: slidesResponse.data.slides,
              totalSlides: slidesResponse.data.total_slides
            }));
          }
        } catch (error) {
          console.log('No PDF slides found, will show PDF directly');
          // PDF slides not available, will show iframe
        }
      }
    } catch (error) {
      console.error('Error loading preview content:', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmDeleteModule = (module) => {
    setModuleToDelete(module);
    setShowDeleteConfirm(true);
  };

  // Stat-Karte "Module" sofort anpassen (Analytics liefert summary.total_modules)
  const decrementModuleStat = () => {
    setAnalytics(prev => {
      if (!prev) return prev;
      if (prev.summary) {
        return { ...prev, summary: { ...prev.summary, total_modules: Math.max(0, (prev.summary.total_modules || 1) - 1) } };
      }
      return { ...prev, total_modules: Math.max(0, (prev.total_modules || 1) - 1) };
    });
  };

  const handleDeleteConfirmed = async () => {
    if (!moduleToDelete) return;
    
    try {
      await axios.delete(`${API}/modules/${moduleToDelete.id}`);
      showSuccessNotification(`Modul "${moduleToDelete.title}" erfolgreich gelöscht`);
      
      // Remove from local state immediately
      setModules(prev => prev.filter(m => m.id !== moduleToDelete.id));

      // Keep category module count in sync (same as backgroundDeleteModule does)
      setCategories(prev => prev.map(cat =>
        cat.id === moduleToDelete.category_id
          ? { ...cat, modules_count: Math.max(0, (cat.modules_count || 1) - 1) }
          : cat
      ));
      decrementModuleStat();
      
      // Clear selection if deleted module was selected
      setSelectedModules(prev => {
        const newSelection = new Set(prev);
        newSelection.delete(moduleToDelete.id);
        return newSelection;
      });
      
    } catch (error) {
      console.error('Delete error:', error);
      showErrorNotification('Fehler beim Löschen des Moduls');
    } finally {
      setShowDeleteConfirm(false);
      setModuleToDelete(null);
    }
  };

  const duplicateModuleAction = async (module) => {
    try {
      const duplicatedModule = {
        title: `${module.title} (Kopie)`,
        description: module.description,
        category_id: module.category_id,
        content_type: module.content_type,
        content: module.content,
        duration_minutes: module.duration_minutes,
        order: (module.order || 0) + 1,
        prerequisites: module.prerequisites || []
      };
      
      const response = await axios.post(`${API}/modules`, duplicatedModule);
      showSuccessNotification(`Modul "${module.title}" erfolgreich dupliziert`);
      
      // Add to local state immediately
      setModules(prev => [...prev, response.data]);
      
    } catch (error) {
      console.error('Duplication error:', error);
      showErrorNotification('Fehler beim Duplizieren des Moduls');
    }
  };

  const toggleModuleStatusAction = async (module) => {
    try {
      const newStatus = !module.is_active;
      await axios.put(`${API}/modules/${module.id}`, {
        is_active: newStatus
      });
      
      showSuccessNotification(`Modul "${module.title}" ${newStatus ? 'aktiviert' : 'deaktiviert'}`);
      
      // Update local state immediately
      setModules(prev => prev.map(m => 
        m.id === module.id 
          ? { ...m, is_active: newStatus }
          : m
      ));
      
    } catch (error) {
      console.error('Status toggle error:', error);
      showErrorNotification('Fehler beim Ändern des Status');
    }
  };

  const getAdvancedModuleStats = async (moduleId) => {
    try {
      // In real implementation, this would be a dedicated analytics endpoint
      const response = await axios.get(`${API}/modules/${moduleId}/analytics`);
      return response.data;
    } catch (error) {
      console.error('Error fetching module analytics:', error);
      return null;
    }
  };

  // Performance: Local cache and background sync
  const [pendingUpdates, setPendingUpdates] = useState(new Map());
  const [isSyncing, setIsSyncing] = useState(false);

  // Filter categories based on showEmptyCategories
  const filteredCategories = showEmptyCategories 
    ? categories 
    : categories.filter(cat => cat.modules_count > 0);

  // Ultra-fast notification helpers
  // Enhanced Toast Notification System
  const [notifications, setNotifications] = useState([]);

  const showSuccessNotification = (message) => {
    const id = Date.now() + Math.random();
    const newNotification = { 
      id, 
      type: 'success', 
      message, 
      icon: '✅'
    };
    
    setNotifications(prev => [...prev.slice(-2), newNotification]); // Max 3 notifications
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3500);
  };

  const showErrorNotification = (message) => {
    const id = Date.now() + Math.random();
    const newNotification = { 
      id, 
      type: 'error', 
      message, 
      icon: '❌'
    };
    
    setNotifications(prev => [...prev.slice(-2), newNotification]); // Max 3 notifications
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  };

  const showInfoNotification = (message) => {
    const id = Date.now() + Math.random();
    const newNotification = { 
      id, 
      type: 'info', 
      message, 
      icon: 'ℹ️'
    };
    
    setNotifications(prev => [...prev.slice(-2), newNotification]); // Max 3 notifications
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 3000);
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Utility function for relative time display
  const getRelativeTime = (dateString) => {
    if (!dateString) return 'vor kurzem';
    
    const now = new Date();
    const date = new Date(dateString);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'gerade eben';
    if (diffMins < 60) return `vor ${diffMins} Minuten`;
    if (diffHours < 24) return `vor ${diffHours} Stunden`;
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    return 'vor einer Woche';
  };

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAdminData();
    }
  }, [user]);

  // Category management helper functions
  const getFilteredCategories = () => {
    let filtered = categories;
    
    // Apply search filter
    if (categorySearchTerm) {
      const searchLower = categorySearchTerm.toLowerCase();
      filtered = filtered.filter(category => 
        category.title.toLowerCase().includes(searchLower) ||
        category.description.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (selectedStatusFilter) {
      if (selectedStatusFilter === 'active') {
        filtered = filtered.filter(category => category.is_active);
      } else if (selectedStatusFilter === 'inactive') {
        filtered = filtered.filter(category => !category.is_active);
      }
    }
    
    // Apply module count filter
    if (selectedModuleCountFilter) {
      if (selectedModuleCountFilter === 'empty') {
        filtered = filtered.filter(category => category.modules_count === 0);
      } else if (selectedModuleCountFilter === 'populated') {
        filtered = filtered.filter(category => category.modules_count > 0);
      }
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (categorySortField) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'modules_count':
          aValue = a.modules_count || 0;
          bValue = b.modules_count || 0;
          break;
        case 'order':
          aValue = a.order || 0;
          bValue = b.order || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        case 'pdf_count':
          aValue = categoryPdfs[a.id]?.length || 0;
          bValue = categoryPdfs[b.id]?.length || 0;
          break;
        case 'completion_rate':
          aValue = getCategoryCompletionRate(a.id);
          bValue = getCategoryCompletionRate(b.id);
          break;
        default:
          aValue = a.order || 0;
          bValue = b.order || 0;
      }
      
      if (aValue < bValue) return categorySortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return categorySortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    
    return filtered;
  };
  
  const handleCategorySort = (field) => {
    if (categorySortField === field) {
      setCategorySortDirection(categorySortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setCategorySortField(field);
      setCategorySortDirection('asc');
    }
  };
  
  const getCategorySortIcon = (field) => {
    if (categorySortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return categorySortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4l9 9 9-9" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 20l-9-9-9 9" />
      </svg>
    );
  };

  const toggleCategorySelection = (categoryId) => {
    const newSelection = new Set(selectedCategories);
    if (newSelection.has(categoryId)) {
      newSelection.delete(categoryId);
    } else {
      newSelection.add(categoryId);
    }
    setSelectedCategories(newSelection);
  };

  const selectAllCategories = () => {
    const allCategoryIds = new Set(getFilteredCategories().map(c => c.id));
    setSelectedCategories(allCategoryIds);
  };

  const deselectAllCategories = () => {
    setSelectedCategories(new Set());
  };

  // Category edit functions
  const handleUpdateCategory = async () => {
    if (!editingCategory || !newCategory.title.trim()) return;

    try {
      const updateData = {
        title: newCategory.title,
        description: newCategory.description,
        color: newCategory.color,
        icon: newCategory.icon,
        order: newCategory.order
      };

      await axios.put(`${API}/categories/${editingCategory}`, updateData);
      
      showSuccessNotification('Kategorie erfolgreich aktualisiert');
      
      // Update local state immediately
      setCategories(prev => prev.map(c => 
        c.id === editingCategory 
          ? { ...c, ...updateData }
          : c
      ));
      
      // Reset form and close modal
      setEditingCategory(null);
      setNewCategory({
        title: '',
        description: '',
        color: '#3B82F6',
        icon: '',
        order: 0,
        target_roles: [],
        visibility_mode: 'cascade'
      });
      
    } catch (error) {
      console.error('Category update error:', error);
      showErrorNotification('Fehler beim Aktualisieren der Kategorie');
    }
  };

  // Category action functions
  const toggleCategoryStatusAction = async (category) => {
    try {
      const newStatus = !category.is_active;
      await axios.put(`${API}/categories/${category.id}`, {
        is_active: newStatus
      });
      
      showSuccessNotification(`Kategorie "${category.title}" ${newStatus ? 'aktiviert' : 'deaktiviert'}`);
      
      // Update local state immediately
      setCategories(prev => prev.map(c => 
        c.id === category.id 
          ? { ...c, is_active: newStatus }
          : c
      ));
      
    } catch (error) {
      console.error('Category status toggle error:', error);
      showErrorNotification('Fehler beim Ändern des Kategorie-Status');
    }
  };

  // Bulk category operations
  const handleBulkCategoryAction = async () => {
    if (selectedCategories.size === 0) return;

    try {
      const categoryIds = Array.from(selectedCategories);
      
      switch (bulkCategoryActionType) {
        case 'delete':
          if (!window.confirm(`${categoryIds.length} Kategorien wirklich löschen? Alle Module werden ebenfalls gelöscht!`)) {
            return;
          }
          for (const categoryId of categoryIds) {
            await axios.delete(`${API}/categories/${categoryId}`);
          }
          showSuccessNotification(`${categoryIds.length} Kategorien erfolgreich gelöscht`);
          break;
          
        case 'activate':
          for (const categoryId of categoryIds) {
            await axios.put(`${API}/categories/${categoryId}`, {
              is_active: true
            });
          }
          showSuccessNotification(`${categoryIds.length} Kategorien aktiviert`);
          break;
          
        case 'deactivate':
          for (const categoryId of categoryIds) {
            await axios.put(`${API}/categories/${categoryId}`, {
              is_active: false
            });
          }
          showSuccessNotification(`${categoryIds.length} Kategorien deaktiviert`);
          break;
          
        case 'colorChange':
          for (const categoryId of categoryIds) {
            await axios.put(`${API}/categories/${categoryId}`, {
              color: bulkCategoryColor
            });
          }
          showSuccessNotification(`${categoryIds.length} Kategorien neu eingefärbt`);
          break;
          
        case 'reorder':
          const selectedCats = categories.filter(c => selectedCategories.has(c.id));
          for (let i = 0; i < selectedCats.length; i++) {
            await axios.put(`${API}/categories/${selectedCats[i].id}`, {
              order: bulkStartPosition + i
            });
          }
          showSuccessNotification(`${categoryIds.length} Kategorien neu positioniert`);
          break;
      }
      
      // Refresh data
      const categoriesRes = await axios.get(`${API}/categories`);
      setCategories(categoriesRes.data);
      
      // Clear selection
      setSelectedCategories(new Set());
      setBulkCategoryActionType('');
      setBulkStartPosition(1);
      setBulkCategoryColor('#3B82F6');
      
    } catch (error) {
      console.error('Bulk category action error:', error);
      showErrorNotification('Fehler bei der Kategorie-Massenoperation');
    }
  };

  // Enhanced category actions
  const duplicateCategoryAction = async (category) => {
    try {
      // Show progress notification
      showInfoNotification(`Dupliziere Kategorie "${category.title}"...`);
      
      // Step 1: Create the duplicated category
      const duplicatedCategory = {
        title: `${category.title} (Kopie)`,
        description: category.description,
        color: category.color,
        icon: category.icon,
        order: (category.order || 0) + 1,
        prerequisites: category.prerequisites || []
      };
      
      const categoryResponse = await axios.post(`${API}/categories`, duplicatedCategory);
      const newCategory = categoryResponse.data;
      
      // Step 2: Get all modules from the original category
      const modulesInCategory = modules.filter(m => m.category_id === category.id);
      
      if (modulesInCategory.length > 0) {
        showInfoNotification(`Kopiere ${modulesInCategory.length} Module...`);
      }
      
      // Step 3: Duplicate each module to the new category
      const duplicatedModules = [];
      for (let i = 0; i < modulesInCategory.length; i++) {
        const module = modulesInCategory[i];
        try {
          const duplicatedModule = {
            title: `${module.title} (Kopie)`,
            description: module.description,
            category_id: newCategory.id, // Assign to new category
            content_type: module.content_type,
            content: module.content,
            media_file: module.media_file,
            duration_minutes: module.duration_minutes,
            order: module.order,
            prerequisites: module.prerequisites || [],
            video_url: module.video_url,
            document_url: module.document_url,
            quiz_questions: module.quiz_questions,
            images: module.images
          };
          
          const moduleResponse = await axios.post(`${API}/modules`, duplicatedModule);
          duplicatedModules.push(moduleResponse.data);
          
          // Show progress for each module
          if (modulesInCategory.length > 3) {
            showInfoNotification(`Module ${i + 1}/${modulesInCategory.length} kopiert...`);
          }
        } catch (moduleError) {
          console.error(`Error duplicating module ${module.title}:`, moduleError);
          showErrorNotification(`Fehler beim Kopieren von Modul "${module.title}"`);
          // Continue with other modules even if one fails
        }
      }
      
      // Step 4: Update category with modules count
      const updatedCategory = {
        ...newCategory,
        modules_count: duplicatedModules.length
      };
      
      showSuccessNotification(
        `🎉 Kategorie-Duplizierung abgeschlossen!\n` +
        `📂 Kategorie: "${category.title} (Kopie)"\n` +
        `📚 ${duplicatedModules.length} von ${modulesInCategory.length} Modulen kopiert`
      );
      
      // Add to local state immediately
      setCategories(prev => [...prev, updatedCategory]);
      setModules(prev => [...prev, ...duplicatedModules]);
      
      // Refresh analytics
      const analyticsRes = await axios.get(`${API}/admin/analytics`);
      setAnalytics(analyticsRes.data);
      
    } catch (error) {
      console.error('Category duplication error:', error);
      showErrorNotification('Fehler beim Duplizieren der Kategorie: ' + (error.response?.data?.detail || error.message));
    }
  };

  const exportCategoryAction = async (category) => {
    try {
      // In real implementation, this would call a backend export endpoint
      const categoryData = {
        ...category,
        modules: modules.filter(m => m.category_id === category.id)
      };
      
      const blob = new Blob([JSON.stringify(categoryData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kategorie_${category.title}.json`;
      a.click();
      URL.revokeObjectURL(url);
      
      showSuccessNotification(`Kategorie "${category.title}" erfolgreich exportiert`);
    } catch (error) {
      console.error('Category export error:', error);
      showErrorNotification('Fehler beim Exportieren der Kategorie');
    }
  };

  const archiveCategoryAction = async (category) => {
    try {
      await axios.put(`${API}/categories/${category.id}`, {
        is_archived: true
      });
      
      showSuccessNotification(`Kategorie "${category.title}" archiviert`);
      
      // Update local state immediately
      setCategories(prev => prev.map(c => 
        c.id === category.id 
          ? { ...c, is_archived: true }
          : c
      ));
      
    } catch (error) {
      console.error('Category archive error:', error);
      showErrorNotification('Fehler beim Archivieren der Kategorie');
    }
  };

  // Analytics functions
  const getCategoryCompletionRate = (categoryId) => {
    const d = categoryAnalyticsData[categoryId];
    return d?.completion_rate ?? 0;
  };

  const getCategoryActiveUsers = (categoryId) => {
    const d = categoryAnalyticsData[categoryId];
    return d ? d.active_users : null;
  };

  const getAverageTimeInCategory = (categoryId) => {
    const d = categoryAnalyticsData[categoryId];
    return d ? d.avg_time_minutes : null;
  };

  const showCategoryAnalyticsModal = async (category) => {
    setSelectedAnalyticsCategory(category);
    setShowCategoryAnalytics(true);
    // Load fresh analytics from backend
    try {
      const res = await axios.get(`${API}/categories/${category.id}/analytics`);
      setCategoryAnalyticsData(prev => ({ ...prev, [category.id]: res.data }));
    } catch (e) {
      console.error('Error fetching category analytics:', e);
    }
  };

  const exportCategoryData = async (category) => {
    try {
      const res = await axios.get(`${API}/categories/${category.id}/export`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `kategorie_${category.title.replace(/ /g,'_')}_analytics.csv`;
      a.click();
      URL.revokeObjectURL(url);
      showSuccessNotification(`CSV für "${category.title}" exportiert`);
    } catch (error) {
      console.error('Export error:', error);
      showErrorNotification('Fehler beim Exportieren der Daten');
    }
  };
  const fetchAdminData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, modulesRes, usersRes, analyticsRes, teamLeadersRes, backofficeRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/modules`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/teamleiters`),
        axios.get(`${API}/backoffice/dashboard`),
      ]);
      
      setCategories(categoriesRes.data);
      setModules(modulesRes.data);
      setUsers(usersRes.data);
      setAnalytics(analyticsRes.data);
      axios.get(`${API}/modules/completion-rates`).then(r => setModuleCompletionRates(r.data)).catch(() => {});
      setTeamLeaders(teamLeadersRes.data);
      setAdminAppointments(backofficeRes.data?.appointments ?? []);
      
      // Load Category PDFs for each category
      const categoryPdfsData = {};
      for (const category of categoriesRes.data) {
        try {
          const pdfsRes = await axios.get(`${API}/categories/${category.id}/pdfs`);
          categoryPdfsData[category.id] = pdfsRes.data;
        } catch (error) {
          categoryPdfsData[category.id] = [];
        }
      }
      setCategoryPdfs(categoryPdfsData);
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Category PDF Management Functions
  const handleOpenPdfModal = (category) => {
    console.log('🟢 handleOpenPdfModal called with category:', category);
    console.log('🟢 Current showPdfModal state:', showPdfModal);
    setSelectedCategoryForPdf(category);
    setPdfUploadFile(null);
    setPdfDescription('');
    setShowPdfModal(true);
    console.log('🟢 showPdfModal set to true');
  };

  const handlePdfUpload = async () => {
    if (!pdfUploadFile || !selectedCategoryForPdf) return;

    setPdfUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', pdfUploadFile);
      if (pdfDescription) {
        formData.append('description', pdfDescription);
      }

      await axios.post(`${API}/categories/${selectedCategoryForPdf.id}/pdfs/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Refresh the PDFs for this category
      const pdfsRes = await axios.get(`${API}/categories/${selectedCategoryForPdf.id}/pdfs`);
      setCategoryPdfs(prev => ({
        ...prev,
        [selectedCategoryForPdf.id]: pdfsRes.data
      }));

      setShowPdfModal(false);
      showSuccessNotification('PDF erfolgreich hochgeladen');
    } catch (error) {
      console.error('Error uploading PDF:', error);
      showErrorNotification('Fehler beim Hochladen der PDF');
    } finally {
      setPdfUploading(false);
    }
  };

  const handleDeleteCategoryPdf = async (pdfId, categoryId) => {
    if (!confirm('Sind Sie sicher, dass Sie diese PDF löschen möchten?')) return;

    try {
      await axios.delete(`${API}/categories/pdfs/${pdfId}`);
      
      // Refresh the PDFs for this category
      const pdfsRes = await axios.get(`${API}/categories/${categoryId}/pdfs`);
      setCategoryPdfs(prev => ({
        ...prev,
        [categoryId]: pdfsRes.data
      }));

      showSuccessNotification('PDF erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting PDF:', error);
      showErrorNotification('Fehler beim Löschen der PDF');
    }
  };

  const handleUpdatePdfDescription = async (pdfId, categoryId, newDescription) => {
    try {
      await axios.put(`${API}/categories/pdfs/${pdfId}`, {
        description: newDescription
      });
      
      // Refresh the PDFs for this category
      const pdfsRes = await axios.get(`${API}/categories/${categoryId}/pdfs`);
      setCategoryPdfs(prev => ({
        ...prev,
        [categoryId]: pdfsRes.data
      }));

      showSuccessNotification('Beschreibung aktualisiert');
    } catch (error) {
      console.error('Error updating PDF description:', error);
      showErrorNotification('Fehler beim Aktualisieren der Beschreibung');
    }
  };

  // PDF Download Function with Authentication
  const handleDownloadCategoryPdf = async (pdfId, filename) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        showErrorNotification('Nicht authentifiziert');
        return;
      }

      // Use fetch to download with authentication
      const response = await fetch(`${API}/categories/pdfs/${pdfId}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status}`);
      }

      // Get the blob data
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || 'download.pdf';
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      showErrorNotification('Fehler beim Herunterladen der PDF');
    }
  };

  const handleAddCategory = async (e) => {
    e.preventDefault();
    try {
      const categoryData = { ...newCategory, order: categories.length + 1 };
      const response = await axios.post(`${API}/categories`, categoryData);
      
      // Optimistic update - add new category immediately
      setCategories([...categories, response.data]);
      fetchAdminData();
      
      setNewCategory({
        title: '',
        description: '',
        color: '#3B82F6',
        icon: '',
        target_roles: [],
        is_mandatory_for_roles: [],
        visibility_mode: 'strict'
      });
      setShowAddCategory(false);
      
      showSuccessNotification('Kategorie erfolgreich erstellt!');
    } catch (error) {
      console.error('Error adding category:', error);
      showErrorNotification('Fehler beim Erstellen der Kategorie: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAddModule = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/modules`, newModule);
      setNewModule({
        title: '',
        description: '',
        category_id: '',
        content_type: 'text',
        content: '',
        duration_minutes: null,
        order: 0
      });
      setShowAddModule(false);
      fetchAdminData();
    } catch (error) {
      console.error('Error adding module:', error);
    }
  };

  const handleResendSetupEmail = async (userId, userEmail) => {
    if (!window.confirm(`Setup-E-Mail erneut an ${userEmail} senden?`)) {
      return;
    }
    
    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/resend-setup-email/${userId}`,
        {},
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        toast.success(`Setup-E-Mail wurde erfolgreich an ${userEmail} gesendet!`);
      }
    } catch (error) {
      console.error('Error resending setup email:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Senden der E-Mail';
      toast.error(errorMsg);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setEditUserData({
      name: user.name,
      email: user.email,
      role: user.role,
      managed_by: user.managed_by || ''
    });
    setShowEditUser(true);
  };

  const handleDeleteUser = (user) => {
    setSelectedUser(user);
    setShowDeleteUser(true);
  };

  const handleDeleteCategory = (category) => {
    setSelectedCategory(category);
    setShowDeleteCategory(true);
  };

  const handleConfirmDeleteCategory = async () => {
    try {
      let deleteParams = {};
      
      if (deleteCategoryOption === 'move' && targetCategoryId) {
        deleteParams.move_to_category_id = targetCategoryId;
      } else if (deleteCategoryOption === 'delete') {
        deleteParams.force_delete = true;
      }
      
      const response = await axios.delete(`${API}/categories/${selectedCategory.id}`, {
        params: deleteParams
      });
      
      setShowDeleteCategory(false);
      setSelectedCategory(null);
      setDeleteCategoryOption('move');
      setTargetCategoryId('');
      fetchAdminData();
      
      // Show success message
      showSuccessNotification(response.data.message || 'Kategorie erfolgreich gelöscht');
    } catch (error) {
      console.error('Error deleting category:', error);
      const errorMsg = error.response?.data?.detail || 'Fehler beim Löschen der Kategorie.';
      showErrorNotification(errorMsg);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      console.log('Updating user with data:', editUserData);
      const response = await axios.put(`${API}/admin/users/${selectedUser.id}`, editUserData);
      console.log('Update response:', response.data);
      setShowEditUser(false);
      setSelectedUser(null);
      fetchAdminData();
    } catch (error) {
      console.error('Error updating user:', error);
      console.error('Error response:', error.response?.data);
    }
  };

  const handleConfirmDeleteUser = async () => {
    try {
      await axios.delete(`${API}/admin/users/${selectedUser.id}`);
      setShowDeleteUser(false);
      setSelectedUser(null);
      fetchAdminData();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // Drag & Drop handlers for categories
  // Ultra-fast category edit with instant UI update + background sync
  const handleEditCategory = async (categoryId, updatedData) => {
    // INSTANT UI UPDATE - no waiting at all
    const updatedCategories = categories.map(cat => 
      cat.id === categoryId ? { ...cat, ...updatedData } : cat
    );
    setCategories(updatedCategories);
    setEditingCategory(null);
    
    // Show success immediately
    showSuccessNotification('Kategorie aktualisiert!');
    
    // Background sync without blocking UI
    backgroundSyncCategory(categoryId, updatedData);
  };

  // Ultra-fast module deletion with instant UI update
  const handleDeleteModule = async (moduleToDelete) => {
    const moduleId = moduleToDelete.id;
    const moduleTitle = moduleToDelete.title;
    
    // INSTANT UI UPDATE - remove from list immediately
    const updatedModules = modules.filter(mod => mod.id !== moduleId);
    setModules(updatedModules);
    setDeletingModule(null);
    
    // INSTANT ANALYTICS UPDATE - update dashboard statistics immediately
    decrementModuleStat();
    
    // Show success immediately
    showSuccessNotification(`Modul "${moduleTitle}" gelöscht!`);
    
    // Background deletion without blocking UI
    backgroundDeleteModule(moduleId, moduleTitle, moduleToDelete);
  };

  const backgroundDeleteModule = async (moduleId, moduleTitle, originalModule) => {
    try {
      const response = await axios.delete(`${API}/modules/${moduleId}`);
      if (response.status === 200) {
        console.log('✅ Module deleted successfully in background');
        
        // Update categories count if needed
        const updatedCategories = categories.map(cat => {
          if (cat.id === originalModule.category_id) {
            return { ...cat, modules_count: Math.max(0, (cat.modules_count || 1) - 1) };
          }
          return cat;
        });
        setCategories(updatedCategories);
        
        // Stat-Karte wurde bereits optimistisch in handleDeleteModule dekrementiert (decrementModuleStat)

        // Optional: Refresh analytics from server for accuracy
        try {
          const analyticsResponse = await axios.get(`${API}/admin/analytics`);
          if (analyticsResponse.status === 200) {
            setAnalytics(analyticsResponse.data);
            console.log('✅ Analytics refreshed from server');
          }
        } catch (analyticsError) {
          console.warn('⚠️ Could not refresh analytics from server:', analyticsError);
        }
      }
    } catch (error) {
      console.error('❌ Background deletion failed:', error);
      
      // Rollback: restore the module
      const restoredModules = [...modules, originalModule].sort((a, b) => a.order - b.order);
      setModules(restoredModules);
      
      // Rollback analytics (optimistic decrement of the "Module" stat card)
      setAnalytics(prev => {
        if (!prev) return prev;
        if (prev.summary) {
          return { ...prev, summary: { ...prev.summary, total_modules: (prev.summary.total_modules || 0) + 1 } };
        }
        return { ...prev, total_modules: (prev.total_modules || 0) + 1 };
      });
      
      showErrorNotification(`Fehler beim Löschen: ${error.response?.data?.detail || 'Modul wurde wiederhergestellt'}`);
    }
  };

  const backgroundSyncCategory = async (categoryId, updatedData) => {
    try {
      // Only send changed fields to minimize data transfer
      const minimalUpdate = {
        title: updatedData.title,
        description: updatedData.description,
        color: updatedData.color,
        icon: updatedData.icon,
        order: updatedData.order
      };
      
      await axios.put(`${API}/categories/${categoryId}`, minimalUpdate);
      console.log('✅ Category synced in background');
    } catch (error) {
      console.error('❌ Background sync failed:', error);
      
      // Revert on error and notify user
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
      showErrorNotification('Sync-Fehler - Daten wiederhergestellt');
    }
  };

  // Ultra-fast module edit - only send changed fields
  const handleEditModule = async (moduleId, updatedData, originalModule) => {
    // INSTANT UI UPDATE
    const updatedModules = modules.map(mod => 
      mod.id === moduleId ? { ...mod, ...updatedData } : mod
    );
    setModules(updatedModules);
    setEditingModule(null);
    
    // Show success immediately
    showSuccessNotification('Modul aktualisiert!');
    
    // Background sync with minimal data
    backgroundSyncModule(moduleId, updatedData, originalModule);
  };

  const backgroundSyncModule = async (moduleId, updatedData, originalModule) => {
    try {
      // Calculate only changed fields to minimize transfer
      const changedFields = {};
      const relevantFields = ['title', 'description', 'content', 'duration_minutes', 'category_id'];
      
      relevantFields.forEach(field => {
        if (updatedData[field] !== originalModule[field]) {
          changedFields[field] = updatedData[field];
        }
      });
      
      console.log('🔄 Syncing only changed fields:', Object.keys(changedFields));
      
      // Use PATCH for partial updates (faster than PUT)
      await axios.patch(`${API}/modules/${moduleId}`, changedFields);
      console.log('✅ Module synced in background');
    } catch (error) {
      console.error('❌ Background module sync failed:', error);
      
      // Fallback: refresh only modules
      try {
        const response = await axios.get(`${API}/modules`);
        setModules(response.data);
        showErrorNotification('Sync-Fehler - Module wiederhergestellt');
      } catch (refreshError) {
        showErrorNotification('Kritischer Fehler - Seite neu laden erforderlich');
      }
    }
  };

  const handleCategoryDragStart = (e, index) => {
    setDraggedCategoryIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleCategoryDrop = async (e, dropIndex) => {
    e.preventDefault();
    if (draggedCategoryIndex === null || draggedCategoryIndex === dropIndex) {
      setDraggedCategoryIndex(null);
      return;
    }
    const filtered = getFilteredCategories();
    const newCategories = [...filtered];
    const draggedCategory = newCategories[draggedCategoryIndex];
    newCategories.splice(draggedCategoryIndex, 1);
    newCategories.splice(dropIndex, 0, draggedCategory);
    const reorderData = newCategories.map((category, index) => ({
      id: category.id,
      order: index + 1
    }));
    try {
      await axios.put(`${API}/categories/reorder`, { items: reorderData });
      fetchAdminData();
    } catch (error) {
      console.error('Error reordering categories:', error);
    }
    setDraggedCategoryIndex(null);
  };

  // Drag & Drop handlers for modules
  const handleModuleDragStart = (e, index) => {
    setDraggedModuleIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleModuleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedModuleIndex === null || draggedModuleIndex === dropIndex) {
      return;
    }

    const newModules = [...modules];
    const draggedModule = newModules[draggedModuleIndex];
    
    // Remove the dragged item
    newModules.splice(draggedModuleIndex, 1);
    
    // Insert at new position
    newModules.splice(dropIndex, 0, draggedModule);
    
    // Update order values
    const reorderData = newModules.map((module, index) => ({
      id: module.id,
      order: index + 1
    }));

    try {
      // Call the reorder API
      await axios.put(`${API}/modules/reorder`, { items: reorderData });
      
      // Refresh data to reflect new order
      fetchAdminData();
    } catch (error) {
      console.error('Error reordering modules:', error);
    }
    
    setDraggedModuleIndex(null);
  };

  // Module search and filter functions
  const getFilteredModules = () => {
    let filtered = [...modules];
    
    // Apply search filter
    if (moduleSearchTerm) {
      const searchLower = moduleSearchTerm.toLowerCase();
      filtered = filtered.filter(module => 
        module.title.toLowerCase().includes(searchLower) ||
        module.description.toLowerCase().includes(searchLower) ||
        module.content_type.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply category filter
    if (selectedCategoryFilter) {
      filtered = filtered.filter(module => 
        module.category_id === parseInt(selectedCategoryFilter)
      );
    }
    
    // Apply type filter
    if (selectedTypeFilter) {
      filtered = filtered.filter(module => 
        module.content_type === selectedTypeFilter
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        case 'content_type':
          aValue = a.content_type.toLowerCase();
          bValue = b.content_type.toLowerCase();
          break;
        case 'category':
          const aCat = categories.find(c => c.id === a.category_id);
          const bCat = categories.find(c => c.id === b.category_id);
          aValue = aCat?.title.toLowerCase() || '';
          bValue = bCat?.title.toLowerCase() || '';
          break;
        case 'order':
          aValue = a.order || 0;
          bValue = b.order || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        case 'completion_rate':
          aValue = getModuleCompletionRate(a.id);
          bValue = getModuleCompletionRate(b.id);
          break;
        default:
          aValue = a.order || 0;
          bValue = b.order || 0;
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });
    
    return filtered;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) {
      return (
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
        </svg>
      );
    }
    
    return sortDirection === 'asc' ? (
      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4l6 6 6-6" />
      </svg>
    ) : (
      <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 20l6-6 6 6" />
      </svg>
    );
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'text':
        return 'bg-blue-100 text-blue-800';
      case 'video':
        return 'bg-red-100 text-red-800';
      case 'pdf':
        return 'bg-purple-100 text-purple-800';
      case 'document':
        return 'bg-green-100 text-green-800';
      case 'quiz':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'text':
        return '📝';
      case 'video':
        return '🎥';
      case 'pdf':
        return '📄';
      case 'document':
        return '📋';
      case 'quiz':
        return '❓';
      default:
        return '📄';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unbekannt';
    
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch (error) {
      return 'Ungültiges Datum';
    }
  };

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto p-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            Zugriff verweigert. Nur Administratoren können auf diese Seite zugreifen.
          </div>
        </div>
      </div>
    );
  }

  // Process Systems Handler Functions for AdminPanel
  const handleCreateSystem = async () => {
    console.log('🚀 AdminPanel handleCreateSystem called with form data:', systemForm);
    try {
      console.log('📡 Making API call to:', `${API}/process-systems`);
      const response = await axios.post(`${API}/process-systems`, systemForm);
      console.log('✅ API response:', response.data);
      setShowSystemModal(false);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
      
      // Refresh systems list to show new system with logo
      if (refreshSystemDetails && refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('System erfolgreich erstellt!');
    } catch (error) {
      console.error('❌ Error creating system:', error);
      console.error('❌ Error response:', error.response?.data);
      showErrorNotification('Fehler beim Erstellen des Systems');
    }
  };

  const handleUpdateSystem = async () => {
    try {
      await axios.put(`${API}/process-systems/${editingSystem.id}`, systemForm);
      setShowSystemModal(false);
      setEditingSystem(null);
      setSystemForm({ name: '', description: '', type: 'client', system_url: '', order: 0, logo_url: '' });
      await fetchAdminData();
      
      // Refresh systems list to show updated system with logo
      if (refreshSystemDetails && refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('System erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating system:', error);
      showErrorNotification('Fehler beim Aktualisieren des Systems');
    }
  };

  const handleCreateGuide = async () => {
    try {
      const guideData = {
        ...guideForm,
        system_id: selectedProcessSystem.id
      };
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/guides`, guideData);
      setShowGuideModal(false);
      setGuideForm({ title: '', content: '', order: 0 });
      
      // Refresh the lists to show the new guide immediately
      if (refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('Anleitung erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating guide:', error);  
      showErrorNotification('Fehler beim Erstellen der Anleitung');
    }
  };

  const handleUpdateGuide = async () => {
    try {
      await axios.put(`${API}/process-guides/${editingGuide.id}`, guideForm);
      setShowGuideModal(false);
      setEditingGuide(null);
      setGuideForm({ title: '', content: '', order: 0 });
      showSuccessNotification('Anleitung erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating guide:', error);
      showErrorNotification('Fehler beim Aktualisieren der Anleitung');
    }
  };

  const handleCreateFaq = async () => {
    try {
      const faqData = {
        ...faqForm,
        system_id: selectedProcessSystem.id
      };
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/faqs`, faqData);
      setShowFaqModal(false);
      setFaqForm({ question: '', answer: '', category: '', order: 0 });
      
      // Refresh the lists to show the new FAQ immediately
      if (refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('FAQ erfolgreich erstellt!');
    } catch (error) {
      console.error('Error creating FAQ:', error);
      showErrorNotification('Fehler beim Erstellen der FAQ');
    }
  };

  const handleUpdateFaq = async () => {
    try {
      await axios.put(`${API}/process-faqs/${editingFaq.id}`, faqForm);
      setShowFaqModal(false);
      setEditingFaq(null);
      setFaqForm({ question: '', answer: '', category: '', order: 0 });
      showSuccessNotification('FAQ erfolgreich aktualisiert!');
    } catch (error) {
      console.error('Error updating FAQ:', error);
      showErrorNotification('Fehler beim Aktualisieren der FAQ');
    }
  };

  const handleUploadDocument = async () => {
    if (!uploadFile) {
      showErrorNotification('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadDescription) {
      formData.append('description', uploadDescription);
    }

    try {
      setUploading(true);
      await axios.post(`${API}/process-systems/${selectedProcessSystem.id}/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setShowDocumentModal(false);
      setUploadFile(null);
      setUploadDescription('');
      
      // Refresh the lists to show the new document immediately
      if (refreshSystemDetails.current) {
        refreshSystemDetails.current();
      }
      
      showSuccessNotification('Dokument erfolgreich hochgeladen!');
    } catch (error) {
      console.error('Error uploading document:', error);
      showErrorNotification('Fehler beim Hochladen des Dokuments');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses Dokument löschen möchten?')) {
      try {
        await axios.delete(`${API}/process-documents/${documentId}`);
        showSuccessNotification('Dokument erfolgreich gelöscht!');
      } catch (error) {
        console.error('Error deleting document:', error);
        showErrorNotification('Fehler beim Löschen des Dokuments');
      }
    }
  };

  const handleDeleteSystem = async (systemId) => {
    if (window.confirm('Sind Sie sicher, dass Sie dieses System löschen möchten?')) {
      try {
        await axios.delete(`${API}/process-systems/${systemId}`);
        if (selectedProcessSystem && selectedProcessSystem.id === systemId) {
          setSelectedProcessSystem(null);
        }
        fetchAdminData();
        showSuccessNotification('System erfolgreich gelöscht!');
      } catch (error) {
        console.error('Error deleting system:', error);
        showErrorNotification('Fehler beim Löschen des Systems');
      }
    }
  };

  // Refresh function to update lists after changes
  const refreshSystemDetails = React.useRef();
  
  const setRefreshFunction = (refreshFn) => {
    refreshSystemDetails.current = refreshFn;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Header />
      
      {/* Notification Toast */}
      {/* Enhanced Toast Notifications with Stacking */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map((notification, index) => (
          <div
            key={notification.id}
            className={`px-6 py-4 rounded-xl shadow-xl transform transition-all duration-500 ease-out animate-slide-in ${
              notification.type === 'success' 
                ? 'bg-green-500/95 text-white' 
                : notification.type === 'error'
                ? 'bg-red-500/95 text-white'
                : 'bg-blue-500/95 text-white'
            }`}
            style={{
              transform: `translateY(${index * 8}px)`,
              zIndex: 50 - index
            }}
          >
            <div className="flex items-center space-x-3">
              <span className="text-lg">{notification.icon}</span>
              <span className="font-medium">{notification.message}</span>
              <button
                onClick={() => removeNotification(notification.id)}
                className="ml-2 text-white/70 hover:text-white transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        ))}
      </div>
      
      <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight mb-1">Admin Dashboard</h1>
          <p className="text-slate-500 text-sm">Verwalten Sie Ihr Training Academy System</p>
        </div>

        {/* Analytics Overview - Always visible */}
        {analytics && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            <div className="stat-card card-enter bg-white rounded-xl p-5 border-l-4 border-l-blue-500 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-blue-600">{analytics.summary?.total_learners ?? analytics.total_users ?? 0}</div>
                  <div className="text-sm text-slate-600 font-medium mt-0.5">Lernende</div>
                  <div className="text-xs text-slate-400 mt-0.5">Vertriebler + Teamleiter</div>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.197m3 2.197c0-.038.003-.076.01-.113M21 21v-1a4 4 0 00-3-3.87" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="stat-card card-enter bg-white rounded-xl p-5 border-l-4 border-l-emerald-500 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-emerald-600">{analytics.summary?.total_categories ?? analytics.total_categories ?? 0}</div>
                  <div className="text-sm text-slate-600 font-medium mt-0.5">Kategorien</div>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl">
                  <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="stat-card card-enter bg-white rounded-xl p-5 border-l-4 border-l-slate-400 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-slate-700">{analytics.summary?.total_modules ?? analytics.total_modules ?? 0}</div>
                  <div className="text-sm text-slate-600 font-medium mt-0.5">Module</div>
                </div>
                <div className="p-3 bg-slate-100 rounded-xl">
                  <svg className="w-6 h-6 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              </div>
            </div>
            <div className="stat-card card-enter bg-white rounded-xl p-5 border-l-4 border-l-amber-500 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-3xl font-bold text-amber-600">
                    {parseFloat(analytics.summary?.avg_completion_pct ?? analytics.completion_rate ?? 0).toFixed(1)}%
                  </div>
                  <div className="text-sm text-slate-600 font-medium mt-0.5">Abschlussrate</div>
                  <div className="text-xs text-slate-400 mt-1">Ø abgeschlossene Module</div>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl">
                  <svg className="w-6 h-6 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Tab Navigation */}
        <div className="bg-white rounded-2xl shadow-lg mb-8 overflow-hidden">
          <div className="border-b border-slate-200 overflow-x-auto scrollbar-thin">
            <nav className="flex gap-6 px-6 min-w-max" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('overview')}
                data-testid="admin-tab-overview"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'overview'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Übersicht</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('users')}
                data-testid="admin-tab-users"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-2.197m3 2.197c0-.038.003-.076.01-.113M21 21v-1a4 4 0 00-3-3.87" />
                  </svg>
                  <span>Benutzer</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('categories')}
                data-testid="admin-tab-categories"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'categories'
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  <span>Kategorien</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('modules')}
                data-testid="admin-tab-modules"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'modules'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>Module</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('analytics')}
                data-testid="admin-tab-analytics"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'analytics'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <span>Analytics</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('termine')}
                data-testid="admin-tab-termine"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'termine'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>Termine</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('erfolge')}
                data-testid="admin-tab-erfolge"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'erfolge'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.5 18.75h-9m9 0a3 3 0 013 3h-15a3 3 0 013-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 01-.982-3.172M9.497 14.25a7.454 7.454 0 00.981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 007.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M18.75 4.236c.982.143 1.954.317 2.916.52A6.003 6.003 0 0016.27 9.728M18.75 4.236V4.5c0 2.108-.966 3.99-2.48 5.228m0 0a6.003 6.003 0 01-2.48.713m4.96 0c-.298.07-.6.128-.908.172" />
                  </svg>
                  <span>Erfolge</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('schulungen')}
                data-testid="admin-tab-schulungen"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'schulungen'
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  <span>Schulungen</span>
                </div>
              </button>
              <button
                onClick={() => setActiveTab('leads')}
                data-testid="admin-tab-leads"
                className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                  activeTab === 'leads'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span>Leads</span>
                </div>
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            <OverviewTab
              activeTab={activeTab}
              analytics={analytics}
              loading={loading}
              notifications={notifications}
              removeNotification={removeNotification}
              setActiveTab={setActiveTab}
            />

            <CategoriesTab
              activeTab={activeTab}
              bulkCategoryActionType={bulkCategoryActionType}
              bulkCategoryColor={bulkCategoryColor}
              bulkStartPosition={bulkStartPosition}
              categories={categories}
              categoryPdfs={categoryPdfs}
              categorySearchTerm={categorySearchTerm}
              deselectAllCategories={deselectAllCategories}
              draggedCategoryIndex={draggedCategoryIndex}
              duplicateCategoryAction={duplicateCategoryAction}
              exportCategoryData={exportCategoryData}
              formatDate={formatDate}
              getCategoryCompletionRate={getCategoryCompletionRate}
              getCategorySortIcon={getCategorySortIcon}
              getFilteredCategories={getFilteredCategories}
              handleAddCategory={handleAddCategory}
              handleBulkCategoryAction={handleBulkCategoryAction}
              handleCategoryDragStart={handleCategoryDragStart}
              handleCategoryDrop={handleCategoryDrop}
              handleCategorySort={handleCategorySort}
              handleDeleteCategory={handleDeleteCategory}
              handleOpenPdfModal={handleOpenPdfModal}
              newCategory={newCategory}
              openPdfListModal={openPdfListModal}
              selectAllCategories={selectAllCategories}
              selectedCategories={selectedCategories}
              selectedModuleCountFilter={selectedModuleCountFilter}
              selectedStatusFilter={selectedStatusFilter}
              setBulkCategoryActionType={setBulkCategoryActionType}
              setBulkCategoryColor={setBulkCategoryColor}
              setBulkStartPosition={setBulkStartPosition}
              setCategorySearchTerm={setCategorySearchTerm}
              setDraggedCategoryIndex={setDraggedCategoryIndex}
              setEditingCategory={setEditingCategory}
              setNewCategory={setNewCategory}
              setSelectedModuleCountFilter={setSelectedModuleCountFilter}
              setSelectedStatusFilter={setSelectedStatusFilter}
              setShowAddCategory={setShowAddCategory}
              setShowEmptyCategories={setShowEmptyCategories}
              showAddCategory={showAddCategory}
              showCategoryAnalyticsModal={showCategoryAnalyticsModal}
              showEmptyCategories={showEmptyCategories}
              toggleCategorySelection={toggleCategorySelection}
              toggleCategoryStatusAction={toggleCategoryStatusAction}
            />

            <ModulesTab
              activeTab={activeTab}
              bulkActionType={bulkActionType}
              bulkTargetCategory={bulkTargetCategory}
              categories={categories}
              confirmDeleteModule={confirmDeleteModule}
              deletingModule={deletingModule}
              deselectAllModules={deselectAllModules}
              duplicateModuleAction={duplicateModuleAction}
              editingModule={editingModule}
              expandedCategories={expandedCategories}
              formatDate={formatDate}
              getFilteredModules={getFilteredModules}
              getModuleCompletionRate={getModuleCompletionRate}
              getTypeColor={getTypeColor}
              getTypeIcon={getTypeIcon}
              handleBulkAction={handleBulkAction}
              handleDeleteModule={handleDeleteModule}
              moduleSearchTerm={moduleSearchTerm}
              modules={modules}
              openPreviewModal={openPreviewModal}
              selectedCategoryFilter={selectedCategoryFilter}
              selectedModules={selectedModules}
              selectedTypeFilter={selectedTypeFilter}
              setAnalytics={setAnalytics}
              setBulkActionType={setBulkActionType}
              setBulkTargetCategory={setBulkTargetCategory}
              setCategories={setCategories}
              setDeletingModule={setDeletingModule}
              setEditingModule={setEditingModule}
              setExpandedCategories={setExpandedCategories}
              setModuleSearchTerm={setModuleSearchTerm}
              setModules={setModules}
              setSelectedCategoryFilter={setSelectedCategoryFilter}
              setSelectedModules={setSelectedModules}
              setSelectedTypeFilter={setSelectedTypeFilter}
              setShowAddModule={setShowAddModule}
              showAddModule={showAddModule}
              showModuleAnalytics={showModuleAnalytics}
              showSuccessNotification={showSuccessNotification}
              toggleModuleSelection={toggleModuleSelection}
              toggleModuleStatusAction={toggleModuleStatusAction}
            />

            <UsersTab
              activeTab={activeTab}
              handleDeleteUser={handleDeleteUser}
              handleEditUser={handleEditUser}
              handleResendSetupEmail={handleResendSetupEmail}
              setShowCreateUser={setShowCreateUser}
              users={users}
            />

            {activeTab === 'processes' && (
              <div className="space-y-6">
                <ProcessSystemsView 
                  user={user} 
                  selectedSystem={selectedProcessSystem} 
                  setSelectedSystem={setSelectedProcessSystem}
                  // Modal states
                  showSystemModal={showSystemModal}
                  setShowSystemModal={setShowSystemModal}
                  showGuideModal={showGuideModal}
                  setShowGuideModal={setShowGuideModal}
                  showFaqModal={showFaqModal}
                  setShowFaqModal={setShowFaqModal}
                  showDocumentModal={showDocumentModal}
                  setShowDocumentModal={setShowDocumentModal}
                  // Form states
                  systemForm={systemForm}
                  setSystemForm={setSystemForm}
                  guideForm={guideForm}
                  setGuideForm={setGuideForm}
                  faqForm={faqForm}
                  setFaqForm={setFaqForm}
                  // Editing states
                  editingSystem={editingSystem}
                  setEditingSystem={setEditingSystem}
                  editingGuide={editingGuide}
                  setEditingGuide={setEditingGuide}
                  editingFaq={editingFaq}
                  setEditingFaq={setEditingFaq}
                  // Upload states
                  uploadFile={uploadFile}
                  setUploadFile={setUploadFile}
                  uploadDescription={uploadDescription}
                  setUploadDescription={setUploadDescription}
                  uploading={uploading}
                  setUploading={setUploading}
                  // Handler functions
                  handleCreateSystem={handleCreateSystem}
                  handleUpdateSystem={handleUpdateSystem}
                  handleCreateGuide={handleCreateGuide}
                  handleUpdateGuide={handleUpdateGuide}
                  handleCreateFaq={handleCreateFaq}
                  handleUpdateFaq={handleUpdateFaq}
                  handleUploadDocument={handleUploadDocument}
                  handleDeleteDocument={handleDeleteDocument}
                  handleDeleteSystem={handleDeleteSystem}
                  // Refresh function to update lists after changes
                  refreshSystemDetails={setRefreshFunction}
                />
              </div>
            )}

            {/* ===== ANALYTICS TAB ===== */}
            {activeTab === 'analytics' && (
              <AnalyticsTab />
            )}

            {/* ===== TERMINE TAB ===== */}
            {activeTab === 'termine' && (
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-gray-800">Termine</h2>
                <BackofficeExamsCalendar
                  appointments={adminAppointments}
                  onResetReschedule={handleResetReschedule}
                  resettingReschedule={resettingReschedule}
                />
              </div>
            )}

            {/* ===== LEADS TAB ===== */}
            {activeTab === 'leads' && (
              <LeadsPage role={user?.role} />
            )}
            {/* ===== SCHULUNGEN TAB ===== */}
            {activeTab === 'schulungen' && (
              <AdminSchulungenView />
            )}
            {/* ===== ERFOLGE TAB ===== */}
            {activeTab === 'erfolge' && (
              <ErfolgeTab role="admin" />
            )}
          </div>
        </div>
        
        {/* Modals remain the same */}
        {/* User Creation Modal */}
        <UserCreationModal
          isOpen={showCreateUser}
          onClose={() => setShowCreateUser(false)}
          onSuccess={fetchAdminData}
        />
        
        {/* Edit User Modal */}
        <UserModals
          editUserData={editUserData}
          handleConfirmDeleteUser={handleConfirmDeleteUser}
          handleUpdateUser={handleUpdateUser}
          selectedUser={selectedUser}
          setEditUserData={setEditUserData}
          setShowDeleteUser={setShowDeleteUser}
          setShowEditUser={setShowEditUser}
          showDeleteUser={showDeleteUser}
          showEditUser={showEditUser}
          teamLeaders={teamLeaders}
        />
        
        {/* Delete User Modal */}

        {/* Delete Category Modal */}
        <CategoryModals
          categories={categories}
          categoryAnalyticsData={categoryAnalyticsData}
          categoryPdfs={categoryPdfs}
          deleteCategoryOption={deleteCategoryOption}
          editingCategory={editingCategory}
          exportCategoryData={exportCategoryData}
          formatDate={formatDate}
          handleConfirmDeleteCategory={handleConfirmDeleteCategory}
          handleDeleteCategoryPdf={handleDeleteCategoryPdf}
          handleDownloadCategoryPdf={handleDownloadCategoryPdf}
          handleOpenPdfModal={handleOpenPdfModal}
          handlePdfUpload={handlePdfUpload}
          handleUpdateCategory={handleUpdateCategory}
          newCategory={newCategory}
          pdfDescription={pdfDescription}
          pdfUploadFile={pdfUploadFile}
          pdfUploading={pdfUploading}
          selectedAnalyticsCategory={selectedAnalyticsCategory}
          selectedCategory={selectedCategory}
          selectedCategoryForPdf={selectedCategoryForPdf}
          setDeleteCategoryOption={setDeleteCategoryOption}
          setEditingCategory={setEditingCategory}
          setNewCategory={setNewCategory}
          setPdfDescription={setPdfDescription}
          setPdfUploadFile={setPdfUploadFile}
          setShowCategoryAnalytics={setShowCategoryAnalytics}
          setShowDeleteCategory={setShowDeleteCategory}
          setShowPdfListModal={setShowPdfListModal}
          setShowPdfModal={setShowPdfModal}
          setTargetCategoryId={setTargetCategoryId}
          showCategoryAnalytics={showCategoryAnalytics}
          showDeleteCategory={showDeleteCategory}
          showPdfListModal={showPdfListModal}
          showPdfModal={showPdfModal}
          targetCategoryId={targetCategoryId}
        />
      </div>
      
      {/* Category PDF Upload Modal */}
      
      {/* PDF Upload Modal */}
      
      {/* PDF List Management Modal */}
      
      {/* Category Edit Modal */}
      
      {/* Phase 3: Category Analytics Modal */}
      
      
      {/* Module Preview Modal - Identical to End User View */}
      <ModuleModals
        handleDeleteConfirmed={handleDeleteConfirmed}
        loadingModuleAnalytics={loadingModuleAnalytics}
        moduleAnalyticsData={moduleAnalyticsData}
        moduleToDelete={moduleToDelete}
        previewModule={previewModule}
        selectedAnalyticsModule={selectedAnalyticsModule}
        setModuleToDelete={setModuleToDelete}
        setPreviewLoading={setPreviewLoading}
        setPreviewModule={setPreviewModule}
        setShowAnalyticsModal={setShowAnalyticsModal}
        setShowDeleteConfirm={setShowDeleteConfirm}
        setShowPreviewModal={setShowPreviewModal}
        showAnalyticsModal={showAnalyticsModal}
        showDeleteConfirm={showDeleteConfirm}
        showErrorNotification={showErrorNotification}
        showPreviewModal={showPreviewModal}
        showSuccessNotification={showSuccessNotification}
      />
      
      {/* Delete Confirmation Modal */}
      
      {/* Phase 3: Module Analytics Modal */}
      
      {/* System Modal */}
      <WikiModals
        editingFaq={editingFaq}
        editingGuide={editingGuide}
        editingSystem={editingSystem}
        faqForm={faqForm}
        guideForm={guideForm}
        handleCreateFaq={handleCreateFaq}
        handleCreateGuide={handleCreateGuide}
        handleCreateSystem={handleCreateSystem}
        handleUpdateFaq={handleUpdateFaq}
        handleUpdateGuide={handleUpdateGuide}
        handleUpdateSystem={handleUpdateSystem}
        handleUploadDocument={handleUploadDocument}
        setEditingFaq={setEditingFaq}
        setEditingGuide={setEditingGuide}
        setEditingSystem={setEditingSystem}
        setFaqForm={setFaqForm}
        setGuideForm={setGuideForm}
        setShowDocumentModal={setShowDocumentModal}
        setShowFaqModal={setShowFaqModal}
        setShowGuideModal={setShowGuideModal}
        setShowSystemModal={setShowSystemModal}
        setSystemForm={setSystemForm}
        setUploadDescription={setUploadDescription}
        setUploadFile={setUploadFile}
        showDocumentModal={showDocumentModal}
        showErrorNotification={showErrorNotification}
        showFaqModal={showFaqModal}
        showGuideModal={showGuideModal}
        showSystemModal={showSystemModal}
        systemForm={systemForm}
        uploadDescription={uploadDescription}
        uploadFile={uploadFile}
        uploading={uploading}
      />
      
      {/* Guide Modal */}
      {/* Enhanced Guide Modal with Step Management */}
      
      {/* FAQ Modal */}
      
      {/* Document Upload Modal */}
    </div>
  );
};

export default AdminPanel;
